import { z } from 'zod';
/**
 * Yjs sync contract shared by the hub (WS-1 server) and the client sync layer.
 *
 * Wire shape: the y-websocket provider connects to `${SYNC_WS_PATH}/${docName}` and speaks
 * the standard y-websocket protocol (sync + awareness) over the socket. The doc name is a
 * path segment rather than a query parameter because y-websocket v3 appends the room name
 * to the server URL.
 */
/**
 * Doc names are constrained so they can travel unescaped in a URL path segment and be
 * validated at the socket boundary (ROADMAP §3: zod-validate every boundary).
 *
 * Valid: `calendar`, `lists`, `household`, `list:abc123`, `dev:scratch`, the two-segment
 * personal doc names `calendar:user:<uuid>` / `lists:user:<uuid>` (WS-15 sharing circles),
 * and — since WS-17c — the three-segment per-match hidden-state name
 * `game:<id>:user:<uuid>` (`@awwyeah/shared/sharing`, `PER_MATCH_USER_DOC_PATTERN`). Three
 * extra segments is the cap: it is what the per-match personal-doc convention needs and
 * nothing in the app wants deeper nesting.
 */
export const syncDocNameSchema = z
    .string()
    .min(1)
    .max(128)
    .regex(/^[a-z][a-z0-9-]*(:[A-Za-z0-9_-]{1,64}){0,3}$/, 'invalid Yjs doc name');
/** Path a client opens for `docName`, relative to the app origin. */
export function syncDocPath(docName, basePath = '/sync') {
    return `${basePath}/${docName}`;
}
/**
 * Extracts the doc name from a WebSocket upgrade pathname, or `null` when the path is not
 * a sync path or the name is invalid. Used by the server before upgrading.
 */
export function parseSyncDocPath(pathname, basePath = '/sync') {
    if (!pathname.startsWith(`${basePath}/`))
        return null;
    const raw = decodeURIComponent(pathname.slice(basePath.length + 1));
    const parsed = syncDocNameSchema.safeParse(raw);
    return parsed.success ? parsed.data : null;
}
/** Connection-level sync state surfaced in the UI (WS-1 client sync layer). */
export const SYNC_STATUSES = ['synced', 'syncing', 'offline'];
//# sourceMappingURL=sync.js.map